import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';

const SolutionCard = ({ tag, tagColor, title, problem, body, link, external = false, muted = false }) => {
  const colorMap = {
    teal: 'bg-[#0E7490]/10 text-[#0E7490]',
    blue: 'bg-[#1A56DB]/10 text-[#1A56DB]',
    purple: 'bg-[#4F46E5]/10 text-[#4F46E5]',
    amber: 'bg-[#B45309]/10 text-[#B45309]',
  };

  return (
    <Card className={`bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-all h-full ${muted ? 'opacity-90 border-dashed' : ''}`}>
      <CardContent className="p-8 flex flex-col h-full">
        {/* Tag Badge */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${colorMap[tagColor]}`}>
            {tag}
          </span>
          {external && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#374151]/10 text-[#6B7280] rounded-full text-xs">
              <ExternalLink size={10} />
              External
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-[#0A1628] mb-4">{title}</h3>
        
        {/* Problem Statement (italic) */}
        <p className="text-[#6B7280] italic mb-4 text-sm leading-relaxed">
          "{problem}"
        </p>
        
        {/* Body Copy */}
        <p className="text-[#374151] text-sm leading-relaxed flex-1 mb-6">
          {body}
        </p>
        
        {/* Link */}
        {external ? (
          <a 
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#1A56DB] font-semibold text-sm hover:underline group"
          >
            Learn More
            <ExternalLink size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        ) : (
          <Link 
            to={link}
            className="inline-flex items-center gap-2 text-[#1A56DB] font-semibold text-sm hover:underline group"
          >
            Learn More
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

const Solutions = () => {
  const solutions = [
    {
      tag: 'Intelligence Foundation',
      tagColor: 'teal',
      title: 'Financial Data Intelligence',
      problem: 'Raw financial data — AA feeds, ITRs, bank statements — is unusable as-is in credit models.',
      body: 'Cognifin transforms every data source into 100+ credit-ready structured features. Automated extraction, validation, and enrichment — for any financial input format.',
      link: '/solutions/financial-data-intelligence',
      external: false,
      muted: false
    },
    {
      tag: 'Operations Layer',
      tagColor: 'blue',
      title: 'Ops Intelligence Suite',
      problem: 'Six critical banking operations still run on manual handoffs and human bottlenecks.',
      body: 'AI agents for CreditOps, KYCOps, FraudOps, PaymentOps, CollectionsOps, and ComplianceOps — governed by a configurable Business Rules Engine.',
      link: '/solutions/ops-intelligence-suite',
      external: false,
      muted: false
    },
    {
      tag: 'Execution Layer',
      tagColor: 'blue',
      title: 'Agentic Co-workers',
      problem: 'Senior underwriters spend hours reading documents, not making credit decisions.',
      body: 'AI co-workers that read every file, triangulate every data source, and draft every CAM — before you open it. You review and decide.',
      link: '/solutions/agentic-coworkers',
      external: false,
      muted: false
    },
    {
      tag: 'Consumer & SME Layer',
      tagColor: 'purple',
      title: 'Financial Intelligence Assistant',
      problem: 'Consumers and SMEs navigate their financial lives with no intelligent layer — just static statements and disconnected products.',
      body: 'A personalised AI co-pilot embedded in your channels — for credit guidance, financial tracking, and proactive advice.',
      link: '/solutions/financial-intelligence-assistant',
      external: false,
      muted: false
    },
    {
      tag: 'Model Risk Management',
      tagColor: 'amber',
      title: 'Aureon (MRM) — A Cognifin Platform',
      problem: 'MRM governance runs on spreadsheets and fails audits.',
      body: 'End-to-end lifecycle governance for credit models, with automated examiner packs and full audit traceability.',
      link: '/solutions/aureon',
      external: false,
      muted: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center" data-testid="solutions-header">
            <h1 className="text-h1 text-white mb-6">
              Five Vertical Solutions. Deep in Every Domain.
            </h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Each solution is a fully deployable product built on the same four-tier intelligence architecture. Pick your entry point.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="section-padding px-6 bg-[#EFF6FF]" data-testid="solutions-grid">
        <div className="max-w-[1200px] mx-auto">
          {/* Top Row - 3 cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {solutions.slice(0, 3).map((solution, index) => (
              <SolutionCard
                key={index}
                tag={solution.tag}
                tagColor={solution.tagColor}
                title={solution.title}
                problem={solution.problem}
                body={solution.body}
                link={solution.link}
                external={solution.external}
                muted={solution.muted}
              />
            ))}
          </div>
          
          {/* Bottom Row - 2 cards centered */}
          <div className="grid md:grid-cols-2 gap-8 max-w-[800px] mx-auto">
            {solutions.slice(3).map((solution, index) => (
              <SolutionCard
                key={index + 3}
                tag={solution.tag}
                tagColor={solution.tagColor}
                title={solution.title}
                problem={solution.problem}
                body={solution.body}
                link={solution.link}
                external={solution.external}
                muted={solution.muted}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Callout Section - Dark Navy */}
      <section className="section-padding px-6 bg-[#0A1628]" data-testid="architecture-callout">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            One Architecture. Across Every Solution.
          </h2>
          <p className="text-body-lg text-[#6B7280] max-w-4xl mx-auto mb-8">
            Every Cognifin solution is built on the same four-tier intelligence architecture: 
            <span className="text-[#0E7490]"> Financial Intelligence Foundation</span> | 
            <span className="text-[#4F46E5]"> Business Rules & Policy Engine</span> | 
            <span className="text-[#1A56DB]"> Agentic Co-workers</span> | 
            <span className="text-[#B45309]"> Human Decision Layer</span>.
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift"
          >
            <Link to="/platform" className="inline-flex items-center gap-2">
              See the Architecture
              <ArrowRight size={20} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
